import React, { useState, useEffect } from 'react';
import apiService from '../../services/apiService';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { cn } from '../../lib/utils';
import { ArrowLeft, ArrowRight, Copy, Check, Info, AlertTriangle, ExternalLink } from 'lucide-react';

interface CodeSnippetStepProps {
  onNext: () => void;
  onBack: () => void;
  onboardingData: {
    siteId?: string;
    siteDomain?: string;
    integrationType?: 'sdk' | 'shopify' | 'woocommerce' | 'manual';
  };
}

const CodeSnippetStep: React.FC<CodeSnippetStepProps> = ({
  onNext,
  onBack,
  onboardingData,
}) => {
  const [apiKey, setApiKey] = useState('');
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadApiKey();
  }, []);

  const loadApiKey = async () => {
    try {
      if (onboardingData.siteId) {
        const response = await apiService.get(`/sites/${onboardingData.siteId}`);
        setApiKey(response.data.apiKey || response.data.site?.apiKey);
      }
    } catch (error) {
      console.error('Failed to load API key:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text: string, index?: number) => {
    try {
      await navigator.clipboard.writeText(text);
      if (index !== undefined) {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
      } else {
        setCopiedKey(true);
        setTimeout(() => setCopiedKey(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getCodeSnippet = () => {
    const { integrationType } = onboardingData;

    switch (integrationType) {
      case 'sdk':
        return {
          title: 'JavaScript SDK Installation',
          steps: [
            {
              label: '1. Add the script tag to your HTML',
              code: `<script src="${process.env.REACT_APP_API_BASE_URL?.replace('/api/v1', '')}/sdk/localization.js"></script>`,
              language: 'html',
            },
            {
              label: '2. Initialize the SDK',
              code: `<script>
  LocalizationHub.init({
    apiKey: '${apiKey}',
    defaultLanguage: 'en',
    autoDetect: true
  });
</script>`,
              language: 'javascript',
            },
            {
              label: '3. Use data-translate attribute',
              code: `<!-- Your existing HTML -->
<h1 data-translate="home.hero.title">Welcome to Our Store</h1>
<p data-translate="home.hero.subtitle">Find the best products</p>

<!-- Will automatically translate based on user's language -->`,
              language: 'html',
            },
          ],
        };

      case 'shopify':
        return {
          title: 'Shopify App Installation',
          steps: [
            {
              label: '1. Install from Shopify App Store',
              code: `Visit: https://apps.shopify.com/localization-hub
Click "Add app" and authorize`,
              language: 'text',
            },
            {
              label: '2. Configure in Shopify Admin',
              code: `1. Go to Apps > LocalizationHub
2. Enter your API key: ${apiKey}
3. Select target languages
4. Click "Enable" to start translating`,
              language: 'text',
            },
          ],
        };

      case 'woocommerce':
        return {
          title: 'WooCommerce Plugin Installation',
          steps: [
            {
              label: '1. Download and install the plugin',
              code: `1. Download: https://downloads.localizationhub.com/woocommerce.zip
2. Go to WordPress Admin > Plugins > Add New
3. Upload and activate the plugin`,
              language: 'text',
            },
            {
              label: '2. Configure in WordPress',
              code: `1. Go to LocalizationHub > Settings
2. Enter API Key: ${apiKey}
3. Select languages and save`,
              language: 'text',
            },
          ],
        };

      case 'manual':
      default:
        return {
          title: 'REST API Integration',
          steps: [
            {
              label: '1. Get translations via API',
              code: `curl -X GET '${process.env.REACT_APP_API_BASE_URL}/translations' \\
  -H 'Authorization: Bearer ${apiKey}' \\
  -H 'Content-Type: application/json'`,
              language: 'bash',
            },
            {
              label: '2. Create a new translation',
              code: `curl -X POST '${process.env.REACT_APP_API_BASE_URL}/translations' \\
  -H 'Authorization: Bearer ${apiKey}' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "languageCode": "es",
    "contentKey": "home.welcome",
    "originalText": "Welcome",
    "translatedText": "Bienvenido"
  }'`,
              language: 'bash',
            },
          ],
        };
    }
  };

  const snippet = getCodeSnippet();

  if (loading) {
    return (
      <CardContent className="p-12 text-center">
        <Skeleton className="h-12 w-12 rounded-full mx-auto mb-4" />
        <Skeleton className="h-6 w-48 mx-auto mb-2" />
        <Skeleton className="h-4 w-64 mx-auto" />
      </CardContent>
    );
  }

  return (
    <CardContent className="p-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full text-3xl mb-4">
            💻
          </div>
          <h2 className="text-3xl font-bold text-foreground mb-2">
            {snippet.title}
          </h2>
          <p className="text-muted-foreground">
            Follow these steps to integrate LocalizationHub into your application
          </p>
        </div>

        {/* API Key Display */}
        <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-0 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Your API Key
                </label>
                <code className="block bg-background px-4 py-3 rounded-lg text-sm font-mono text-foreground border">
                  {apiKey}
                </code>
              </div>
              <Button
                onClick={() => handleCopy(apiKey)}
                variant={copiedKey ? 'default' : 'secondary'}
                className={cn("ml-4 flex-shrink-0", copiedKey && "bg-green-500 hover:bg-green-600")}
              >
                {copiedKey ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <div className="mt-3 flex items-start text-xs text-muted-foreground">
              <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0 text-yellow-500" />
              Keep this key secure! It provides access to your site's translations.
            </div>
          </CardContent>
        </Card>

        {/* Code Snippets */}
        <div className="space-y-6 mb-8">
          {snippet.steps.map((step, index) => (
            <Card key={index} className="overflow-hidden">
              <div className="bg-muted/50 px-6 py-3 border-b flex items-center justify-between">
                <h3 className="font-semibold text-foreground">{step.label}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy(step.code, index)}
                  className={cn(
                    "text-sm",
                    copiedIndex === index ? "text-green-600" : "text-primary"
                  )}
                >
                  {copiedIndex === index ? (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1" />
                      Copy code
                    </>
                  )}
                </Button>
              </div>
              <div className="bg-zinc-900 p-6 overflow-x-auto">
                <pre className="text-sm text-zinc-100 font-mono whitespace-pre-wrap">
                  {step.code}
                </pre>
              </div>
            </Card>
          ))}
        </div>

        {/* Help Box */}
        <Card className="bg-primary/5 border-0 mb-8">
          <CardContent className="p-6">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-foreground mb-2">Need help?</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Check out our detailed documentation or contact support if you get stuck.
                </p>
                <div className="flex space-x-4">
                  <a
                    href="/docs"
                    className="text-sm text-primary hover:underline font-medium flex items-center"
                  >
                    View Docs
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                  <a
                    href="/support"
                    className="text-sm text-primary hover:underline font-medium flex items-center"
                  >
                    Get Support
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-6 border-t">
          <Button type="button" variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <Button onClick={onNext}>
            Continue
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </CardContent>
  );
};

export default CodeSnippetStep;
